import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonIcon,
  Dropdown,
  DropdownTarget,
  DropdownSource,
  DropdownContext,
  DropdownContent,
} from '@brandwatch/axiom-components';

import InviteMessage from '../InviteMessage.jsx';
import './InfoConsoleButton.css';
import getReadStatusKeyName from '../../utils/getReadStatusKeyName.js';

const baseFontSize = parseInt(
  window.getComputedStyle(document.documentElement)
    .getPropertyValue('font-size')
    .replace('px', '')
  , 10);

function calculateDropdownWidth() {
  // Screen is narrower than 25rem + 16px margin.
  // This is happening in code because Axiom.
  if (window.screen && window.screen.width < baseFontSize * 25 + 32) {
    return '90vw';
  }
  return '25rem';
}

const InfoButton = ({isUnread, onClick = () => {}}) => {
  return (
    <div className={`InfoConsoleButton ${isUnread ? 'InfoConsoleButton--unread' : ''}`}
         data-viz-at="button-info">
      <Button shape="circle" size="medium" style="quaternary" onClick={onClick}>
        <img src="https://cdn.vizia.brandwatch.com/assets/details.svg" width="16px" height="16px" />
      </Button>
    </div>
  );
};

InfoButton.propTypes = {
  isUnread: PropTypes.bool,
  onClick: PropTypes.func
};

class InfoConsoleButton extends React.PureComponent {
  constructor(props) {
    super(props);

    this.buttonEl = React.createRef();

    this.isDropdownOpen = false;

    const {invite} = this.props;

    this.state = {
      dropdownWidth: calculateDropdownWidth(),
      hasReadInvite: !invite.message || localStorage.getItem(getReadStatusKeyName(invite))
    };

    this.handleDropdownRequestOpen = this.handleDropdownRequestOpen.bind(this);
    this.handleDropdownRequestClose = this.handleDropdownRequestClose.bind(this);
    this.handleInfoClick = this.handleInfoClick.bind(this);
    this.handleOnBlur = this.handleOnBlur.bind(this);
    this.handleOrientationChange = this.handleOrientationChange.bind(this);
  }

  componentDidMount() {
    window.addEventListener('orientationchange', this.handleOrientationChange);

    // Automatically show the invite message to users who haven't read it before.
    if (!this.state.hasReadInvite) {
      this.buttonEl.current.click();
      window.addEventListener('blur', this.handleOnBlur);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('blur', this.handleOnBlur);
    window.removeEventListener('orientationchange', this.handleOrientationChange);
  }

  handleOrientationChange() {
    this.setState({dropdownWidth: calculateDropdownWidth()});
  }

  handleOnBlur() {
    // Close the invite message when the user clicks on a component.
    if (document.activeElement.tagName === 'IFRAME') {
      this.buttonEl && this.buttonEl.current.click();
    }
  }

  handleInfoClick() {
    this.props.toggleInfoConsole();
  }

  handleDropdownRequestOpen() {
    // The flag will be set after the invite message has been automatically shown.
    if (!this.isDropdownOpen) {
      this.isDropdownOpen = true;
      return;
    }

    // For some reason handleDropdownRequestOpen is fired when the user clicks
    // on the dropdown target (despite the dropdown being already open). So
    // this mimics the desired behaviour of closing the dropdown (the state will
    // be updated, causing the dropdown to disappear on re-render).
    this.handleDropdownRequestClose();
  }

  handleDropdownRequestClose() {
    // Remember that the user has read the invite message for this invite,
    // for when / if they return to this invite in future.
    const keyName = getReadStatusKeyName(this.props.invite);
    localStorage.setItem(keyName, new Date().toISOString());

    this.setState({hasReadInvite: true});

    // No longer needed because the invite message is closed now.
    window.removeEventListener('blur', this.handleOnBlur);
  }

  render() {
    const {invite} = this.props;

    if (this.state.hasReadInvite) {
      return (
        <InfoButton onClick={this.handleInfoClick} />
      );
    }

    // Render the invite message as a speech bubble for users who haven't
    // read it before (the invite message will be shown automatically once the
    // component has mounted).
    return (
      <Dropdown
        onRequestOpen={this.handleDropdownRequestOpen}
        onRequestClose={this.handleDropdownRequestClose}
        position="top"
        showArrow={true}
      >
        <DropdownTarget>
          <div ref={this.buttonEl}>
            <InfoButton isUnread={true} />
          </div>
        </DropdownTarget>
        <DropdownSource>
          <DropdownContext width={this.state.dropdownWidth}>
            <DropdownContent padding="medium">
              <InviteMessage invite={invite} />
            </DropdownContent>
          </DropdownContext>
        </DropdownSource>
      </Dropdown>
    );
  }
}

InfoConsoleButton.propTypes = {
  toggleInfoConsole: PropTypes.func.isRequired,
  invite: PropTypes.object.isRequired,
};

export default InfoConsoleButton;
